/**
 * External Imports
 */

import { connect, useDispatch } from 'react-redux';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
/**
 * Nexus Imports
 */
import { NeuIcon } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react';
/**
 * Internal Imports
 */
import { logReportsClick, ReportEvents } from '../../../utils/analyticsHelpers';
import TransitionalBarChart from '../../metricsChart/barChart/TransitionalBarChart';
import { RootState } from '../../../redux/store';
import { setFeedbackFilter } from '../../../redux/actions';
/**
 * Global Type Definition Imports
 */
import { ApiMetrics, Filter, MetricsData } from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  HeaderContainer,
  TaskMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';

interface CoachingMetricChartProps {
  metrics: ApiMetrics;
  patientSummaryReportFilter: Filter;
}

const CoachingMetricChart: FC<CoachingMetricChartProps> = ({
  metrics,
  patientSummaryReportFilter
}) => {
  const dispatch = useDispatch();
  const navigateTo = useNavigate();
  const { '*': view } = useParams();
  const { logTrackingEvent } = useAnalyticsApi();

  const metricsData: MetricsData = useMemo(() => {
    return {
      totalRecordsCount: metrics.totalRecordsCount,
      metrics: [
        {
          label: 'Opened',
          value: metrics.openedRecordsCount,
          color: '#df5828'
        },
        {
          label: 'Completed',
          value: metrics.completedRecordsCount,
          color: '#0077b5'
        }
      ]
    };
  }, [metrics]);

  const openFeedbackTable = () => {
    dispatch(
      setFeedbackFilter({
        ...patientSummaryReportFilter,
        taskSubType: 'coaching',
        startDate:
          patientSummaryReportFilter.startDate ??
          format(new Date(), 'yyyy-MM-dd'),
        category: '',
        subCategory: '',
        createdBy: '',
        patient: '',
        employeeId: ''
      })
    );
    if (view)
      logTrackingEvent(
        logReportsClick(view?.replace('/', '-'), 'coaching' as ReportEvents)
      );
    setTimeout(() => {
      navigateTo('/patients/feedback', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <TaskMetricContainer onClick={openFeedbackTable}>
      <HeaderContainer style={{ paddingLeft: '3.5em' }}>
        <NeuIcon size="1x" color="purple" className="ms-1">
          campaign
        </NeuIcon>
        <TitleContainer
          data-testid="patient-coaching-metric-label"
          style={{ marginLeft: '0.5rem' }}
        >
          {metrics.totalRecordsCount} Coaching
        </TitleContainer>
      </HeaderContainer>
      <TransitionalBarChart metricsData={metricsData} />
    </TaskMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    patientSummaryReportFilter: state.ReportReducer.patientSummaryReportFilter,
    metrics: state.TaskReducer.coachingMetrics
  };
};
export default connect(mapReduxStateToProps)(CoachingMetricChart);
